/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { Fragment, Suspense } from 'react';
import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import CommonUtils from '../../../common/utils/CommonUtils';
import * as constants from '../../../common/constants';
import env from '../../../common/env';
import LoadingCard from '../Loader/LoadingCard';
import EreaderContainer from '../EreaderContainer';
import IframeWrapper from '../../../common/components/iframeWrapper/IframeWrapper';
import ExpandCollapseButton from '../../../common/components/IntegratedLetfNavBar/ExpandCollapseButton';

const LogoBackBar = React.lazy(() => import('../../../common/components/logoBackBar/LogoBackBar'));


const IntegratedAuthHomeContainer = React.lazy(() => import('../IntegratedAuthHomeContainer'));

/**
 * Global Nav plugin
 *
 * @author Gladson Samuel S
 */

export default class GlobalNavPlugin {
  constructor(context, route = constants.HOME_ROUTE) {
    this.context = context;
    this.route = route;
    this.init();
  }

  /**
   * Initialize GlobalNavPlugin
   *
   */
  init = () => {
    Framework.getEventManager().on(constants.TOGGLE_LEFT_NAV, this.handleDrawer);
  };

  /**
   * Returns the current route of the app
   *
   * @returns
   */
  getSelectedMenu = () => {
    const {
      location
    } = this.context.props;
    const homeMatch = CommonUtils.getMatchedRoute(
      [...constants.HOME_ROUTES, ...constants.HOME2_ROUTES],
      location
    );
    const courseRouteMatch = CommonUtils.getMatchedRoute(constants.COURSE_ROUTES(this.route), location);
    const productRouteMatch = CommonUtils.getMatchedRoute(constants.PRODUCT_ROUTES(this.route), location);
    const studyPrepMatch = CommonUtils.getMatchedRoute(constants.STUDY_PREP_ROUTES(this.route), location);

    if (homeMatch?.isExact) {
      return constants.LEFT_NAV_MENU_KEY.HOME;
    }

    if (productRouteMatch?.isExact || courseRouteMatch?.isExact) {
      return constants.LEFT_NAV_MENU_KEY.ETEXT;
    }

    if (studyPrepMatch?.isExact) {
      return constants.LEFT_NAV_MENU_KEY.STUDYPREP;
    }

    return constants.LEFT_NAV_MENU_KEY.HOME;
  };

  /**
   * Sets (selectedMenu) state
   *
   * @param {String} selectedMenu
   * @returns
   */
  setSelectedMenu = (selectedMenu) => {
    if (!selectedMenu) {
      return;
    }

    this.context.setState({ selectedMenu });
  };

  /**
   * Sets globalEmbeddedUrl required to launch study-prep route
   *
   * @param {string} embeddedUrl
   */
  updateGlobalEmbeddedUrl = (embeddedUrl, redirectFrom) => {
    const herobanner = getSnapshot(Framework.getStoreRegistry().getStore('herobanner'));
    const channelId = herobanner.channelId;
    let URL = embeddedUrl || env.STUDY_CHANNEL_URL;
    if (channelId && !embeddedUrl) {
      URL = `${CommonUtils.getChannelLink(channelId, constants.CHANNEL_HYPER_LINKS.LEARN)}?${constants.STUDY_PREP_QUERY_PARAM}`;
    }
    if (redirectFrom === constants.AUTH_HOME && URL && typeof URL === 'string') {
      URL = CommonUtils.addQueryParameterWithHash(URL, constants.IFRAME_QUERY_STRING);
    }
    this.context.globalEmbeddedUrl = URL;
  };

  /**
   * Handle Left nav menu click
   *
   * @param {String} leftMenu
   * @param {String} embeddedUrl
   */
  handleSetSelectedMenu = (leftMenu, embeddedUrl = '', redirectFrom = constants.AUTH_HOME) => {
    this.setSelectedMenu(leftMenu);
    if (leftMenu === constants.LEFT_NAV_MENU_KEY.STUDYPREP) {
      this.context.setState({ isDrawerOpen: true });
      this.updateGlobalEmbeddedUrl(embeddedUrl, redirectFrom);
    }

    /**
       * Redirect to different route based on active menu
       */
    let isShowChatbot = false;
    switch (leftMenu) {
      case constants.LEFT_NAV_MENU_KEY.HOME:
        Framework.redirectTo(this.route);
        document.title = CommonUtils.getAppTitle();
        isShowChatbot = true;
        break;
      case constants.LEFT_NAV_MENU_KEY.ETEXT:
        this.context.heroDashboardPlugin.handleCtaClick();
        break;
      case constants.LEFT_NAV_MENU_KEY.STUDYPREP:
        Framework.redirectTo(`${this.route}/channels/study-prep`);
        break;
      default:
        break;
    }
    GlobalNavPlugin.toggleGetHelpChat(isShowChatbot);

    this.context.heroDashboardPlugin.tiggerABTestingForGlobalNavEvent(leftMenu, true);
  };

  /**
   * Sets (isDrawerOpen) state
   */
  handleDrawer = () => {
    const {
      isDrawerOpen
    } = this.context.state || {};
    const leftMenu = isDrawerOpen ? constants.LEFT_NAV_MENU_EVENT_LABEL.NAV_CLOSE : constants.LEFT_NAV_MENU_EVENT_LABEL.NAV_OPEN;
    this.context.heroDashboardPlugin.tiggerABTestingForGlobalNavEvent(leftMenu, true);
    this.context.setState({ isDrawerOpen: !isDrawerOpen });
  };

  /**
   * Close drawer when user opens eReader
   *
   */
  closeDrawer = () => {
    this.context.setState({ isDrawerOpen: false });
  };

  /**
   * Close QR Code Modal when user close it from eReader
   *
   */
  closeQRCodeModal = () => {
    this.context.setState(state => ({
      modalProps: {
        ...state.modalProps,
        open: !state.modalProps.open
      }
    }));
  };

  /**
   * Handles Route change
   *
   */
  handleRouteChange = () => {
    const currentSelectedMenu = this.getSelectedMenu();
    const {
      selectedMenu
    } = this.context.state || {};

    if (selectedMenu !== currentSelectedMenu) {
      this.setSelectedMenu(currentSelectedMenu);
    }
  };

  /**
   * Returns study prep iframe
   *
   * @param {String} embeddedUrl
   * @returns {React.ReactElement}
   */
  renderStudyPrep = (embeddedUrl) => {
    const {
      location
    } = this.context.props;
    const {
      isDrawerOpen
    } = this.context.state || {};
    const studyPrepMatch = CommonUtils.getMatchedRoute(constants.STUDY_PREP_ROUTES(this.route), location);

    if (!studyPrepMatch?.isExact) {
      return null;
    }

    if (!embeddedUrl) {
      Framework.redirectTo(this.route);
      return null;
    }

    return (
      <>
        {!isDrawerOpen ? (
          <LogoBackBar
            onLogoClick={this.context.onPearsonLogoClick}
            renderExpandCollapseButton={GlobalNavPlugin.renderExpandCollapseButton()}
          />
        ) : '' }
        <IframeWrapper
          embeddedUrl={embeddedUrl}
          closeDrawer={this.closeDrawer}
        />
      </>
    );
  };

  /**
   * Returns global nav content based on route
   *
   * @returns {React.ReactElement}
   */
  renderContent = () => {
    const currentSelectedMenu = this.getSelectedMenu();
    this.handleRouteChange();

    if (currentSelectedMenu === constants.LEFT_NAV_MENU_KEY.HOME) {
      return this.context.renderAuthHome();
    }

    if (currentSelectedMenu === constants.LEFT_NAV_MENU_KEY.ETEXT) {
      return (
        <EreaderContainer
          route={this.route}
          onUnmount={this.context.initiateCommonPlugin}
          onMount={this.closeDrawer}
          isDrawerOpen={this.context.state.isDrawerOpen}
          closeQRCodeModal={this.closeQRCodeModal}
        />
      );
    }

    return (
      <Fragment>
        {this.renderStudyPrep(this.context.globalEmbeddedUrl)}
      </Fragment>
    );
  };

  /**
   * Renders Integrated AuthHome
   *
   * @returns {React.ReactElement}
   */
  renderIntegratedAuthHome = () => {
    const isIntegratedUI = this.context.commonPlugin.isIntegratedUI();
    const { template = constants.HERO_BANNER_TYPES.MARKETING_TEMPLATE } = this.context.heroDashboardPlugin.getActiveCourseTab() || {};
    const {
      isDrawerOpen,
      selectedMenu
    } = this.context.state || {};

    if (!isIntegratedUI) {
      return null;
    }

    return (
      <Suspense fallback={<LoadingCard location={constants.AUTH_HOME} />}>
        <IntegratedAuthHomeContainer
          isDrawerOpen={isDrawerOpen}
          selectedMenu={selectedMenu}
          handleDrawer={this.handleDrawer}
          handleSetSelectedMenu={this.handleSetSelectedMenu}
          template={template}
        >
          {this.renderContent()}
        </IntegratedAuthHomeContainer>
      </Suspense>
    );
  };

  /**
   * Render expand collapse button
   *
   * @returns
   */
  static renderExpandCollapseButton = (isAHDrawerOpen = false) => {
    if (isAHDrawerOpen) {
      return null;
    }

    return (
      <ExpandCollapseButton
        classes={{
          tooltip: 'variant1',
          tooltipArrow: 'variant1'
        }}
        isMenuOpen={false}
        onClick={() => {
          Framework.getEventManager().publish(constants.TOGGLE_LEFT_NAV);
        }}
      />
    );
  };

  static toggleGetHelpChat = (show = false) => {
    const chatbotEle = document.getElementById(constants.GET_HELP_BUTTON_ID);

    if (!chatbotEle) {
      return;
    }

    // eslint-disable-next-line no-undef
    const isChatbotHidden = chatbotEle?.style?.display === 'none' || getComputedStyle(chatbotEle)?.display === 'none';

    if (isChatbotHidden && show) {
      chatbotEle.style.display = 'block';
    } else {
      chatbotEle.style.display = 'none';
    }
  };

  /**
   * Handle post message
   *
   * @param {*} action
   * @returns
   */
  handlePostMessage = (action) => {
    if (!action) {
      return;
    }

    switch (action) {
      case constants.CHANNEL_POST_MESSAGE_ACTION.LOGOACTION:
        Framework.redirectTo(this.route);
        break;
      case constants.CHANNEL_POST_MESSAGE_ACTION.LOGOUT:
        window.piSession.setOptions({
          requireLogin: true
        });
        Framework.logout();
        break;
      default:
        break;
    }
  };
}
